import React, { useState, useMemo, useEffect } from 'react'
import { Search, BarChart3, User, ExternalLink, TrendingUp, Loader2, CheckCircle, Heart, MessageCircle, Share2, Briefcase } from 'lucide-react'
import { useGetCampaignsQuery, GetHitsDocument } from 'graphql/generated/graphql'
import { useApolloClient } from '@apollo/client'

// Utility function for relative time
const getRelativeTime = (dateString: string): string => {
  const date = new Date(Number(dateString))
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`
  return `${Math.floor(diffInSeconds / 2592000)} months ago`
}

// Utility function to format numbers (1.2K, 1.5M, etc.)
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  }
  return num.toString()
}

// Platform icons using simple colored circles with letters
const PlatformIcon = ({ platform }: { platform: string }) => {
  const platformColors: Record<string, string> = {
    Instagram: 'from-pink-500 to-purple-600',
    Facebook: 'from-blue-600 to-blue-700',
    TikTok: 'from-black to-cyan-400',
    Youtube: 'from-red-600 to-red-700',
    Reddit: 'from-orange-500 to-orange-600',
  }

  const platformInitials: Record<string, string> = {
    Instagram: 'IG',
    Facebook: 'FB',
    TikTok: 'TT',
    Youtube: 'YT',
    Reddit: 'RD',
  }

  return (
    <div className={`w-10 h-10 bg-gradient-to-br ${platformColors[platform]} rounded-full flex items-center justify-center text-white font-bold text-xs`}>
      {platformInitials[platform]}
    </div>
  )
}

interface ProfileInteraction {
  id: string
  platform: string
  username: string
  name: string
  avatarUrl: string
  interactions: number
  lastInteraction: string
  bio?: string
  followers?: number
  following?: number
  postCount?: number
  verified?: boolean
  totalLikes: number
  totalComments: number
  totalShares: number
  postURL: string
  campaignId: string
  campaignName: string
  campaigns: Set<string>
}

export default function AllInteractionsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all')
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'interactions' | 'recent'>('interactions')
  const [allHits, setAllHits] = useState<any[]>([])
  const [fetchingHits, setFetchingHits] = useState(false)
  const [fetchError, setFetchError] = useState<any>(null)

  const platforms = ['all', 'Instagram', 'Facebook', 'TikTok', 'Youtube', 'Reddit']
  const client = useApolloClient()

  // Fetch all campaigns first
  const { data: campaignsData, loading: campaignsLoading, error: campaignsError } = useGetCampaignsQuery({
    variables: {
      filter: {
        search: '', // Required field - empty string gets all campaigns
      },
    },
    fetchPolicy: 'network-only',
  })

  // Get all campaign IDs
  const campaignIds = useMemo(() => 
    campaignsData?.campaigns?.map(c => Number(c.id)) ?? [], 
    [campaignsData?.campaigns]
  )
  // Fetch hits from ALL campaigns with batching to avoid overwhelming the backend
  useEffect(() => {
    if (!campaignsData?.campaigns || campaignIds.length === 0) {
      setAllHits([])
      return
    }

    const fetchAllCampaignHits = async (): Promise<void> => {
      setFetchingHits(true)
      setFetchError(null)
      
      try {
        const BATCH_SIZE = 3 // Fetch 3 campaigns at a time to avoid overwhelming backend
        const allFetchedHits: any[] = []

        // Process campaigns in batches
        for (let i = 0; i < campaignIds.length; i += BATCH_SIZE) {
          const batch = campaignIds.slice(i, i + BATCH_SIZE)
          
          const batchPromises = batch.map(async (campaignId) => {
            try {
              return await client.query({
                query: GetHitsDocument,
                variables: {
                  filter: {
                    campaignId,
                  },
                },
                fetchPolicy: 'network-only',
              })
            } catch (err) {
              console.error(`Error fetching hits for campaign ${campaignId}:`, err)
              // Return empty result for this campaign but continue with others
              return { data: { hits: { hits: [] } } }
            }
          })

          const batchResults = await Promise.all(batchPromises)
          const batchHits = batchResults.flatMap(result => result.data?.hits?.hits ?? [])
          allFetchedHits.push(...batchHits)

          // Add small delay between batches to be nice to the backend
          if (i + BATCH_SIZE < campaignIds.length) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }
        
        setAllHits(allFetchedHits)
      } catch (err) {
        console.error('Error fetching hits from campaigns:', err)
        setFetchError(err)
      } finally {
        setFetchingHits(false)
      }
    }

    void fetchAllCampaignHits()
}, [campaignIds, client])

  const loading = campaignsLoading || fetchingHits
  const error = campaignsError ?? fetchError

  // Use allHits instead of data from a single query
  const data = { hits: { hits: allHits } }

  // Transform hits data into profile interactions (only interacted hits)
  const profileInteractions = useMemo(() => {
    if (!data?.hits?.hits) return []

    // Filter for only interacted hits first
    const interactedHits = data.hits.hits.filter(hit => hit.interactedWith)
    
    const profileMap = new Map<string, ProfileInteraction>()

    interactedHits.forEach((hit) => {
      if (!hit.date) return // Skip hits without dates
      
      const creatorKey = `${String(hit.creator.name)}-${String(hit.platforms[0])}`
      
      if (profileMap.has(creatorKey)) {
        const existing = profileMap.get(creatorKey)
        if (existing) {
          existing.interactions += 1
          existing.totalLikes += Number(hit.interaction?.positive ?? 0)
          existing.totalComments += Number(hit.interaction?.comment ?? 0)
          existing.totalShares += Number(hit.interaction?.shared ?? 0)
          existing.campaigns.add(hit.campaignName ?? 'Unknown Campaign')
          
          const currentDate = new Date(Number(hit.date))
          const existingDate = new Date(Number(existing.lastInteraction))
          if (currentDate > existingDate) {
            existing.lastInteraction = hit.date
            existing.campaignId = String(hit.campaignId ?? '')
            existing.campaignName = hit.campaignName ?? 'Unknown Campaign'
            existing.postURL = hit.postURL
          }
        }
      } else {
        const campaigns = new Set<string>()
        campaigns.add(hit.campaignName ?? 'Unknown Campaign')
        
        profileMap.set(creatorKey, {
          id: creatorKey,
          platform: hit.platforms[0],
          username: hit.creator.name,
          name: hit.creator.name,
          avatarUrl: hit.creator.avatarUrl ?? 'https://i.pravatar.cc/150',
          interactions: 1,
          lastInteraction: hit.date,
          bio: hit.interaction?.bio ?? undefined,
          followers: hit.interaction?.followers ?? undefined,
          following: hit.interaction?.following ?? undefined,
          postCount: hit.interaction?.postCount ?? undefined,
          verified: hit.interaction?.verified ?? false,
          totalLikes: Number(hit.interaction?.positive ?? 0),
          totalComments: Number(hit.interaction?.comment ?? 0),
          totalShares: Number(hit.interaction?.shared ?? 0),
          postURL: hit.postURL,
          campaignId: String(hit.campaignId ?? ''),
          campaignName: hit.campaignName ?? 'Unknown Campaign',
          campaigns,
        })
      }
    })

    return Array.from(profileMap.values())
  }, [data])

  // Get unique campaigns for filter
  const campaigns = useMemo(() => {
    const campaignSet = new Set<string>()
    profileInteractions.forEach(profile => {
      profile.campaigns.forEach(campaign => campaignSet.add(campaign))
    })
    return ['all', ...Array.from(campaignSet).sort()]
  }, [profileInteractions])

  // Calculate statistics
  const stats = useMemo(() => {
    const totalInteractions = profileInteractions.reduce((sum, item) => sum + item.interactions, 0)
    const platformCounts = profileInteractions.reduce<Record<string, number>>((acc, item) => {
      acc[item.platform] = (acc[item.platform] ?? 0) + item.interactions
      return acc
    }, {})

    const campaignCounts = profileInteractions.reduce<Record<string, number>>((acc, item) => {
      item.campaigns.forEach(campaign => {
        acc[campaign] = (acc[campaign] ?? 0) + 1
      })
      return acc
    }, {})

    return {
      total: totalInteractions,
      uniqueProfiles: profileInteractions.length,
      byPlatform: platformCounts,
      byCampaign: campaignCounts,
      totalCampaigns: campaigns.length - 1, // Subtract 'all'
    }
  }, [profileInteractions, campaigns])

  // Filter and sort interactions
  const filteredInteractions = useMemo(() => {
    let filtered = profileInteractions

    // Filter by platform
    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(item => item.platform === selectedPlatform)
    }

    // Filter by campaign
    if (selectedCampaign !== 'all') {
      filtered = filtered.filter(item => item.campaigns.has(selectedCampaign))
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(item =>
        item.username.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query)
      )
    }

    // Sort
    if (sortBy === 'interactions') {
      filtered = [...filtered].sort((a, b) => b.interactions - a.interactions)
    }

    return filtered
  }, [profileInteractions, selectedPlatform, selectedCampaign, searchQuery, sortBy])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-purple-300 mb-2">All Campaigns Analytics</h1>
          <p className="text-gray-400">View interaction analytics across all your campaigns</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-12 text-center">
            <Loader2 className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-300">
              {campaignsLoading 
                ? 'Loading campaigns...' 
                : `Loading interactions from ${campaignIds.length} campaign${campaignIds.length !== 1 ? 's' : ''}...`
              }
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-red-500/20 p-6 text-center">
            <div className="text-red-400 mb-2">Failed to load interactions</div>
            <p className="text-gray-400 text-sm">{error.message}</p>
          </div>
        )}

        {/* Content - only show when not loading and no error */}
        {!loading && !error && (
          <>
        {/* Success message showing all campaigns loaded */}
        {campaignIds.length > 1 && allHits.length > 0 && (
          <div className="bg-green-900/20 backdrop-blur-xl rounded-xl shadow-xl border border-green-500/30 p-4 mb-6">
            <div className="flex items-center space-x-2 text-green-300 text-sm">
              <CheckCircle className="w-5 h-5" />
              <span>
                Successfully loaded interactions from <strong>{campaignIds.length} campaigns</strong>
              </span>
            </div>
          </div>
        )}
        {campaignIds.length === 0 && (
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-12 text-center mb-6">
            <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-gray-300 mb-2">No Campaigns Found</h3>
            <p className="text-gray-500 text-sm">
              Create a campaign first to start tracking interactions.
            </p>
          </div>
        )}
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-6 hover:border-purple-400/40 transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Total Interactions</span>
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats.total}</div>
            <div className="flex items-center text-green-400 text-xs">
              <TrendingUp className="w-3 h-3 mr-1" />
              <span>Active engagement</span>
            </div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Unique Profiles</span>
              <User className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats.uniqueProfiles}</div>
            <div className="text-gray-400 text-xs">Across all campaigns</div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-pink-500/20 p-6 hover:border-pink-400/40 transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Top Platform</span>
              <div className="text-pink-400 text-xs font-medium">
                {Object.entries(stats.byPlatform).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'N/A'}
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {Math.max(...Object.values(stats.byPlatform))}
            </div>
            <div className="text-gray-400 text-xs">interactions</div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Active Campaigns</span>
              <Briefcase className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats.totalCampaigns}</div>
            <div className="text-gray-400 text-xs">with interactions</div>
          </div>
        </div>

        {/* Platform Breakdown */}
        <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-6 mb-6">
          <h3 className="text-purple-300 font-medium mb-4">Platform Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats.byPlatform).map(([platform, count]) => (
              <div key={platform} className="flex flex-col items-center">
                <PlatformIcon platform={platform} />
                <span className="text-gray-300 text-sm mt-2">{platform}</span>
                <span className="text-purple-400 font-bold text-lg">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-900/50 border border-purple-500/20 rounded-lg pl-10 pr-4 py-2 text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-500"
              />
            </div>

            {/* Campaign Filter */}
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="bg-slate-700/50 border border-purple-500/30 rounded-lg px-4 py-2 text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {campaigns.map(campaign => (
                <option key={campaign} value={campaign} className="bg-slate-700">
                  {campaign === 'all' ? 'All Campaigns' : campaign}
                </option>
              ))}
            </select>

            {/* Platform Filter */}
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="bg-slate-700/50 border border-purple-500/30 rounded-lg px-4 py-2 text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {platforms.map(platform => (
                <option key={platform} value={platform} className="bg-slate-700">
                  {platform === 'all' ? 'All Platforms' : platform}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'interactions' | 'recent')}
              className="bg-slate-700/50 border border-purple-500/30 rounded-lg px-4 py-2 text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="interactions" className="bg-slate-700">Most Interactions</option>
              <option value="recent" className="bg-slate-700">Most Recent</option>
            </select>
          </div>

          {/* Active filters indicator */}
          {(selectedPlatform !== 'all' || selectedCampaign !== 'all' || searchQuery.trim()) && (
            <div className="mt-3 pt-3 border-t border-purple-500/20 flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                <span className="text-xs text-gray-400">Active filters:</span>
                {selectedCampaign !== 'all' && (
                  <span className="bg-green-600/20 text-green-300 px-2 py-1 rounded-full text-xs border border-green-500/30">
                    {selectedCampaign}
                  </span>
                )}
                {selectedPlatform !== 'all' && (
                  <span className="bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full text-xs border border-purple-500/30">
                    {selectedPlatform}
                  </span>
                )}
                {searchQuery.trim() && (
                  <span className="bg-cyan-600/20 text-cyan-300 px-2 py-1 rounded-full text-xs border border-cyan-500/30">
                    Search: &quot;{searchQuery}&quot;
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedPlatform('all')
                  setSelectedCampaign('all')
                }}
                className="text-xs text-gray-400 hover:text-gray-200 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <span className="text-gray-400 text-sm">
            Showing {filteredInteractions.length} profile{filteredInteractions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Profiles List */}
        {filteredInteractions.length === 0 ? (
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-12 text-center">
            <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-gray-300 mb-2">No profiles found</h3>
            <p className="text-gray-500 text-sm">
              {searchQuery.trim() ? 'Try adjusting your search or filters' : 'Start interacting with profiles to see them here'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInteractions.map((profile) => (
              <div
                key={profile.id}
                className="bg-slate-800/60 backdrop-blur-xl rounded-xl shadow-xl border border-purple-500/20 p-5 hover:border-purple-400/40 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={profile.avatarUrl}
                      alt={profile.name}
                      className="w-12 h-12 rounded-full border-2 border-purple-500/30"
                      onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        const target = e.target as HTMLImageElement
                        target.src = 'https://i.pravatar.cc/150'
                      }}
                    />
                    <div>
                      <div className="flex items-center space-x-1">
                        <span className="font-medium text-gray-100">{profile.name}</span>
                        {profile.verified && (
                          <CheckCircle className="w-4 h-4 text-blue-400 fill-current" />
                        )}
                      </div>
                      <div className="text-sm text-gray-400">@{profile.username}</div>
                    </div>
                  </div>
                  <PlatformIcon platform={profile.platform} />
                </div>

                {/* Campaign Tags */}
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1">
                    {Array.from(profile.campaigns).slice(0, 2).map((campaign) => (
                      <span
                        key={campaign}
                        className="bg-green-600/20 text-green-300 px-2 py-0.5 rounded-full text-xs border border-green-500/30"
                      >
                        {campaign}
                      </span>
                    ))}
                    {profile.campaigns.size > 2 && (
                      <span className="bg-green-600/20 text-green-300 px-2 py-0.5 rounded-full text-xs border border-green-500/30">
                        +{profile.campaigns.size - 2} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Engagement Metrics */}
                {(profile.totalLikes > 0 || profile.totalComments > 0 || profile.totalShares > 0) && (
                  <div className="flex items-center justify-around py-3 mb-3 bg-slate-900/50 rounded-lg border border-purple-500/10">
                    {profile.totalLikes > 0 && (
                      <div className="flex items-center space-x-1 text-pink-400">
                        <Heart className="w-4 h-4" />
                        <span className="text-xs font-medium">{formatNumber(profile.totalLikes)}</span>
                      </div>
                    )}
                    {profile.totalComments > 0 && (
                      <div className="flex items-center space-x-1 text-cyan-400">
                        <MessageCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">{formatNumber(profile.totalComments)}</span>
                      </div>
                    )}
                    {profile.totalShares > 0 && (
                      <div className="flex items-center space-x-1 text-green-400">
                        <Share2 className="w-4 h-4" />
                        <span className="text-xs font-medium">{formatNumber(profile.totalShares)}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-2xl font-bold text-purple-300">{profile.interactions}</div>
                    <div className="text-xs text-gray-400">interactions</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Last active</div>
                    <div className="text-sm text-gray-300">{getRelativeTime(profile.lastInteraction)}</div>
                  </div>
                </div>

                <button 
                  onClick={() => window.open(profile.postURL, '_blank')}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-purple-500 hover:to-pink-500 transition-all duration-200 flex items-center justify-center space-x-2 group">
                  <span>View Profile</span>
                  <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  )
}